import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@utils/axios_api';
import { toast } from 'react-toastify';
import { useContext } from 'react';
import { AuthContext } from '@context/AuthContext';

/**
 * 역할/권한/메뉴 관리 훅
 * - 전체 권한 목록 조회
 * - 역할별 권한 코드 목록 조회 및 저장
 * - 역할별 메뉴 코드 목록 조회 및 저장
 * - 전체 메뉴 트리 조회
 */
const useRolePermission = (selectedRoleCode) => {
    const queryClient = useQueryClient();
    const { auth: { roleId: myRoleId }, refreshPermissions } = useContext(AuthContext);

    // ── 전체 권한 목록 ──────────────────────────────────────────────────
    const { data: allPermissions = [], isLoading: permLoading } = useQuery({
        queryKey: ['allPermissions'],
        queryFn: () => get('/super/comm/permission/list').then(res => res?.result ?? []),
        staleTime: 10 * 60 * 1000,
    });

    // ── 선택된 역할의 권한 코드 목록 ────────────────────────────────────
    const { data: rolePermCodes = [], isLoading: rolePermLoading } = useQuery({
        queryKey: ['rolePermissions', selectedRoleCode],
        queryFn: () => get(`/super/comm/permission/rolePermissions?roleCode=${selectedRoleCode}`)
            .then(res => res?.result ?? []),
        enabled: !!selectedRoleCode,
    });

    // ── 전체 메뉴 트리 ──────────────────────────────────────────────────
    const { data: menuTree = [], isLoading: menuLoading } = useQuery({
        queryKey: ['allMenus'],
        queryFn: () => get('/super/comm/menu/list').then(res => res?.result ?? []),
        staleTime: 10 * 60 * 1000,
    });

    // ── 선택된 역할의 메뉴 코드 목록 ────────────────────────────────────
    const { data: roleMenuCodes = [], isLoading: roleMenuLoading } = useQuery({
        queryKey: ['roleMenus', selectedRoleCode],
        queryFn: () => get(`/super/comm/menu/roleMenus?roleCode=${selectedRoleCode}`)
            .then(res => res?.result ?? []),
        enabled: !!selectedRoleCode,
    });

    // ── 역할-권한 저장 ──────────────────────────────────────────────────
    const savePermsMutation = useMutation({
        mutationFn: ({ roleCode, permCodes }) =>
            put('/super/comm/permission/rolePermissions', { roleCode, permCodes }),
        onSuccess: (_, { roleCode }) => {
            toast.success('권한이 저장되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['rolePermissions', selectedRoleCode] });
            // 저장한 역할이 현재 로그인 사용자의 역할이면 권한 즉시 갱신
            if (roleCode === myRoleId) {
                refreshPermissions();
            }
        },
        onError: () => toast.error('권한 저장에 실패했습니다.'),
    });

    // ── 역할-메뉴 저장 ──────────────────────────────────────────────────
    const saveMenusMutation = useMutation({
        mutationFn: ({ roleCode, menuCodes }) =>
            put('/super/comm/menu/roleMenus', { roleCode, menuCodes }),
        onSuccess: () => {
            toast.success('메뉴가 저장되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['roleMenus', selectedRoleCode] });
            queryClient.invalidateQueries({ queryKey: ['myMenus']}); // 동적 메뉴 즉시 갱신
        },
        onError: () => toast.error('메뉴 저장에 실패했습니다.'),
    });

    return {
        allPermissions,
        rolePermCodes,
        menuTree,
        roleMenuCodes,
        isLoading: permLoading || rolePermLoading || menuLoading || roleMenuLoading,
        savePerms: (roleCode, permCodes) => savePermsMutation.mutate({ roleCode, permCodes }),
        saveMenus: (roleCode, menuCodes) => saveMenusMutation.mutate({ roleCode, menuCodes }),
        isSaving: savePermsMutation.isPending || saveMenusMutation.isPending,
    };
};

export default useRolePermission;
